import { MEDIEN_LOG_TYPEN } from '../../domain/medienzahl';
import { criterionKey, taskScore, type Assessment } from '../../domain/scoring';
import { scaleStep, toNumber, type Fraction } from '../../domain/points';
import type { Task } from '../../domain/types';
import type { TaskIndex } from '../../persistence/repos';
import type { ExamLogRow } from '../../persistence/rows';
import { logTypeId, type LogTypeName } from '../../persistence/seed';
import {
  kennungGlobalerAntwort,
  rohkennung,
  verteileWortindexe,
  zeilenAusRoh,
} from './kennung';
import type { ClassicZeilenbewertung, ClassicStationsbewertung, ClassicAufgabenbewertung } from './typen';

/**
 * Aus dem Bewertungsstand werden Classic-Ratings — **rein**: kein Netz, keine Ablage, keine Uhr.
 *
 * ## Warum der Stand und nicht die Ereignisse
 *
 * Relay führt einen Ereignisstrom: jede Punktvergabe eine Zeile, jede Rücknahme eine weitere.
 * Classic führt Zustandszeilen — eine je (Prüfling, Aufgabe), in der Punkte **und** Kommentar
 * nebeneinander stehen. Und die Auswertung des Servers nimmt je (Prüfling, Prüfer) **nur das
 * jüngste** Rating.
 *
 * ⚠️ **Daraus folgt die Regel, um die sich dieses Modul dreht:** ein Rating trägt immer den
 * *vollständigen* Stand einer Bewertung, nie nur die Änderung. Wer einen nachgereichten
 * Kommentar allein schickt, schickt ein Rating mit `points = 0.0` — dem Vorgabewert des Servers
 * —, und weil dieses Rating das jüngste ist, sind die vorher übertragenen Punkte aus der
 * Wertung verschwunden. Der Prüfer sieht davon nichts; erst die Excel-Auswertung zeigt es.
 *
 * Der Stand kommt deshalb aus `Assessment` — demselben, aus dem der Bogen gezeichnet wird
 * (`repos.ts::loadAssessment`) —, und die offenen Logzeilen sagen nur, **welche** Bewertungen
 * hinausgehen müssen, nicht was in ihnen steht.
 *
 * ## Warum eine Zeile in zwei Gruppen stehen darf
 *
 * Eine `ANSWER_RATING`-Zeile ändert die Punkte ihrer Antwortzeile **und** die Summe der
 * Aufgabe, gehört also zu zwei Ratings. Sie wird in beiden Gruppen genannt und darf erst
 * gestempelt werden, wenn **beide** quittiert sind (`senden.ts`). Stempelte die Zeilenbewertung
 * allein, bliebe eine verlorene Aufgabenbewertung-Quittung für immer unbemerkt: beim nächsten Takt
 * wäre nichts mehr offen, und die Aufgabensumme auf dem Server bliebe veraltet.
 */

export interface RatingGruppe {
  art: 'task' | 'answer' | 'station';
  /** Die negative Kennung, unter der das Rating hinausgeht. */
  id: number;
  /** Welche offenen Logzeilen dieses Rating abdeckt. Darf sich mit anderen Gruppen überschneiden. */
  logIds: string[];
  lastUpdate: string;
}

/** Eine Zeile, die nicht hinausgeht — mit Grund, damit niemand rätselt. */
export interface Liegengeblieben {
  logId: string;
  grund: string;
  /**
   * Ob das erwartbar ist. `true` bei Ereignissen, für die Classic schlicht kein Feld hat
   * (Anmeldungen, Stationswechsel) und bei Aufnahmen, die Etappe 3 braucht — solche Zeilen
   * gehören nicht in eine Warnung an den Prüfer. `false` heisst: hier stimmt etwas nicht.
   */
  erwartet: boolean;
}

export interface Bewertungsstapel {
  taskratings: ClassicAufgabenbewertung[];
  answerratings: ClassicZeilenbewertung[];
  /**
   * Bleibt vorerst leer: `Stationsbewertung` trägt nur Kommentare, und unser Bogen kennt keinen
   * Kommentar auf Stationsebene — `TEXT_COMMENT` verweist immer auf ein `EXAM_ITEM`. Das Feld
   * steht hier, damit der Weg fertig ist, wenn der Anlass kommt.
   */
  stationratings: ClassicStationsbewertung[];
  /** Je Rating die Zuordnung für den Stempel, in derselben Reihenfolge wie die Listen oben. */
  gruppen: RatingGruppe[];
  liegengeblieben: Liegengeblieben[];
}

export interface BewertungsEingabe {
  /** Die offenen Zeilen **dieser** Prüfung und dieses Prüflings. */
  offen: ExamLogRow[];
  /** Der vollständige Stand — auch das, was längst übertragen ist. */
  assessment: Assessment;
  tasks: Task[];
  index: TaskIndex;
  /** `ClassicKennung`: unsere Kennung → Rohkennung. Für die globale Antwortzeile. */
  kennungen: Map<string, string>;
  candidateID: string;
  examinerID: string | null;
  actorID: string | null;
  /** Liefert die nächste (negative) Kennung des Laufs. */
  naechsteId: () => number;
  /**
   * Die Handskizzen als PNG-Data-URL, je Aufgabe bzw. Station (Schlüssel wie in
   * `assessment.comments`). Gerendert wird ausserhalb — hier gibt es keine Leinwand.
   */
  skizzen?: ReadonlyMap<string, string>;
}

const TYP = (name: LogTypeName) => logTypeId(name);

/** Welche Logtypen zu einem Rating führen — und welche ausdrücklich nicht. */
const PUNKTE_ODER_TEXT = new Set([
  TYP('ANSWER_RATING'),
  TYP('QUESTION_RATING'),
  TYP('TEXT_COMMENT'),
  TYP('SELECTED_WORDS'),
]);

/**
 * Aufnahmen: der Weg dafür ist der Datei-Upload aus Etappe 3.
 *
 * ⚠️ **Die Handschrift gehört seit dem 2026-09-14 dazu.** Sie hat einen eigenen Typ bekommen
 * (vorher lag sie in `IMAGE_COMMENT`); fehlte sie hier, fiele sie in den Zweig darunter und
 * bekäme die irreführende Begründung „Für dieses Ereignis hat Classic kein Feld". Liegen
 * bleibt sie so oder so — nur der Grund im Protokoll unterscheidet sich.
 */
const AUFNAHMEN = new Set(
  [...MEDIEN_LOG_TYPEN].map((name) => TYP(name)),
);

/**
 * Die markierten Wortgruppen einer Aufgabe, aufsteigend — aus dem **Stand**, nicht aus dem
 * `value` der Logzeile. Dieselbe Ableitung wie in `repos.ts`, nur andersherum.
 */
function wortIndexe(a: Assessment, taskId: string): number[] {
  return Object.keys(a.pickedWords)
    .filter((k) => a.pickedWords[k] && k.startsWith(taskId + '#'))
    .map((k) => Number(k.slice(taskId.length + 1)))
    .sort((x, y) => x - y);
}

/** Die Punkte einer einzelnen Antwortzeile aus dem Stand. */
function zeilenPunkte(
  task: Task,
  a: Assessment,
  position: number,
  max: Fraction,
  steps: number,
): { punkte: Fraction; meta: string | null } {
  if (steps === 0) {
    const gehakt = Boolean((a.checked[task.id] ?? [])[position]);
    return {
      punkte: gehakt ? max : { n: 0, d: 1 },
      meta: JSON.stringify({ selected: gehakt }),
    };
  }
  const stufe = a.criteria[criterionKey(task.id, position)];
  if (stufe == null) {
    // Zurückgenommen oder nie gesetzt: 0 Punkte, und **kein** erfundener Stufenindex.
    return { punkte: { n: 0, d: 1 }, meta: null };
  }
  return {
    punkte: scaleStep(max, stufe, steps),
    meta: JSON.stringify({ index: stufe }),
  };
}

export function baueBewertungen(eingabe: BewertungsEingabe): Bewertungsstapel {
  const stapel: Bewertungsstapel = {
    taskratings: [],
    answerratings: [],
    stationratings: [],
    gruppen: [],
    liegengeblieben: [],
  };

  const examineeID = rohkennung(eingabe.candidateID);
  const examinerID = rohkennung(eingabe.examinerID);
  const actorID = rohkennung(eingabe.actorID) ?? -1;

  /** Alles liegenlassen, mit einem Grund, der für alle betroffenen Zeilen gilt. */
  const liegenLassen = (zeilen: ExamLogRow[], grund: string, erwartet = false) => {
    for (const z of zeilen) stapel.liegengeblieben.push({ logId: z.id, grund, erwartet });
  };

  if (examineeID === null) {
    liegenLassen(eingabe.offen, 'Der Prüfling trägt keine Classic-Kennung (' + eingabe.candidateID + ').');
    return stapel;
  }
  if (examinerID === null) {
    // Ohne Prüfer fehlt die halbe Hälfte des Auswertungsschlüssels — der Server ordnete das
    // Rating niemandem zu.
    liegenLassen(eingabe.offen, 'Zu diesen Bewertungen ist kein Prüfer bekannt.');
    return stapel;
  }

  const taskVon = new Map(eingabe.tasks.map((t) => [t.id, t]));

  /**
   * Welche Aufgaben und Antwortzeilen betroffen sind, und welche offenen Zeilen dahinterstehen.
   * Eine Aufgabe kann auch dann betroffen sein, wenn nur eine ihrer Antwortzeilen offen ist:
   * ihre Summe hat sich mitgeändert.
   */
  const zuAufgabe = new Map<string, ExamLogRow[]>();
  const zuStation = new Map<string, ExamLogRow[]>();
  const zuAntwort = new Map<string, ExamLogRow[]>();
  const eintragen = (karte: Map<string, ExamLogRow[]>, schlüssel: string, z: ExamLogRow) => {
    const bisher = karte.get(schlüssel);
    if (bisher) bisher.push(z);
    else karte.set(schlüssel, [z]);
  };

  /** Von der Kennung einer Antwortzeile zurück auf ihre Aufgabe und ihre Position darin. */
  const antwortGehoertZu = new Map<string, { itemId: string; position: number }>();
  /** Von der Kennung einer Frage zurück auf ihre Aufgabe. */
  const frageGehoertZu = new Map<string, string>();
  for (const [itemId, eintrag] of eingabe.index) {
    frageGehoertZu.set(eintrag.questionId, itemId);
    eintrag.answers.forEach((antwort, position) => {
      antwortGehoertZu.set(antwort.id, { itemId, position });
    });
  }

  for (const z of eingabe.offen) {
    if (AUFNAHMEN.has(z.examLogTypeID)) {
      /*
       * Die Datei geht über den Upload; die Bewertung ihrer Aufgabe (oder Station) muss aber
       * neu hinaus, denn sie trägt `commentAudio`/`commentVideos` und die Skizze
       * (`commentImage`). Ohne diese Zeile käme eine nachgereichte Aufnahme beim Server nie
       * als Kennzeichen an (2026-10-09).
       */
      if (z.referenceType === 'EXAM_ITEM_CONTAINER') eintragen(zuStation, z.referenceIdentifier ?? '', z);
      else eintragen(zuAufgabe, z.referenceIdentifier ?? '', z);
      continue;
    }
    if (!PUNKTE_ODER_TEXT.has(z.examLogTypeID)) {
      // Anmeldungen, Stationswechsel, „Aufgabe angesehen" — Classic führt sie nicht einmal in
      // seiner eigenen Ablage. Sie bleiben offen (und gingen nach einem Wechsel in den
      // Relay-Modus doch noch hinaus), sind aber kein Fehler.
      liegenLassen([z], 'Für dieses Ereignis hat Classic kein Feld.', true);
      continue;
    }

    const ziel = z.referenceIdentifier ?? '';

    if (z.examLogTypeID === TYP('SELECTED_WORDS')) {
      const itemId = frageGehoertZu.get(ziel);
      if (!itemId) {
        liegenLassen([z], 'Zu dieser Frage ist keine Aufgabe bekannt (' + ziel + ').');
        continue;
      }
      /*
       * ⚠️ Die Wortmarkierung hängt in Classic am `Zeilenbewertung` der Antwortzeile — auch bei
       * globaler Bewertung, wo Relay gar keine `ExamAnswer` führt. Die Kennung dieser Zeile
       * steht deshalb in `ClassicKennung` (vom Download gemerkt, s. `abbildung.ts`).
       */
      const roh = eingabe.kennungen.get(kennungGlobalerAntwort(itemId));
      if (!roh) {
        liegenLassen(
          [z],
          'Die Antwortzeile der global bewerteten Aufgabe ist nicht bekannt — die Prüfung ' +
            'wurde vor Etappe 2 geladen. Ein erneutes Laden holt sie nach.',
        );
        continue;
      }
      eintragen(zuAntwort, kennungGlobalerAntwort(itemId), z);
      continue;
    }

    if (z.examLogTypeID === TYP('ANSWER_RATING')) {
      const treffer = antwortGehoertZu.get(ziel);
      if (!treffer) {
        liegenLassen([z], 'Zu dieser Antwortzeile ist keine Aufgabe bekannt (' + ziel + ').');
        continue;
      }
      eintragen(zuAntwort, ziel, z);
      // Die Summe der Aufgabe hat sich mitgeändert — dasselbe tut der Classic-Client, der neben
      // jedem Zeilenbewertung auch ein Aufgabenbewertung schreibt.
      eintragen(zuAufgabe, treffer.itemId, z);
      continue;
    }

    /*
     * ⚠️ **Ein `TEXT_COMMENT` kann auch auf die Station zeigen** — seit dem 2026-09-13 gibt es
     * den Stationskommentar, und er benutzt denselben Log-Typ mit `referenceType`
     * `EXAM_ITEM_CONTAINER` (s. `persistence/repos.ts`). Ohne diese Weiche landete er in der
     * Aufgabengruppe und liesse dort eine Aufgabe suchen, die es nicht gibt.
     */
    if (z.referenceType === 'EXAM_ITEM_CONTAINER') {
      eintragen(zuStation, ziel, z);
      continue;
    }

    // `QUESTION_RATING` und `TEXT_COMMENT` zeigen beide auf das `EXAM_ITEM` und landen
    // deshalb in **derselben** Gruppe: Punkte und Kommentar gehören in ein Rating.
    eintragen(zuAufgabe, ziel, z);
  }

  const juengstes = (zeilen: ExamLogRow[]) =>
    zeilen.reduce((max, z) => (z.creationDate > max ? z.creationDate : max), zeilen[0].creationDate);

  /**
   * Prüfer und Schauspieler **je Bewertung**: aus ihrer jüngsten Zeile, die einen trägt. Bis
   * zum 2026-10-09 galt einer für alle Bewertungen eines Prüflings — bewerteten zwei Prüfer am
   * selben Gerät denselben Prüfling, landete alles bei einem.
   */
  const ausZeilen = (zeilen: ExamLogRow[], feld: 'examinerID' | 'actorID'): string | null => {
    let bester: ExamLogRow | null = null;
    for (const z of zeilen) if (z[feld] && (!bester || z.creationDate > bester.creationDate)) bester = z;
    return bester ? (bester[feld] ?? null) : null;
  };
  const pruefer = (zeilen: ExamLogRow[]) => rohkennung(ausZeilen(zeilen, 'examinerID')) ?? examinerID;
  const schauspieler = (zeilen: ExamLogRow[]) => {
    const eigen = ausZeilen(zeilen, 'actorID');
    return eigen ? (rohkennung(eigen) ?? -1) : actorID;
  };

  /** Die Kommentarfelder einer Aufgabe oder Station aus dem Stand. */
  const kommentarFelder = (schluessel: string) => {
    const kasten = eingabe.assessment.comments[schluessel];
    return {
      commentImage: eingabe.skizzen?.get(schluessel) ?? null,
      commentAudio: kasten && kasten.audios.length > 0 ? ('Y' as const) : ('N' as const),
      commentScans: 'N' as const,
      commentVideos: kasten && kasten.videos.length > 0 ? ('Y' as const) : ('N' as const),
    };
  };

  /* ── Aufgabenbewertungen ── */

  for (const [itemId, zeilen] of zuAufgabe) {
    const taskID = rohkennung(itemId);
    const task = taskVon.get(itemId);
    if (taskID === null) {
      liegenLassen(zeilen, 'Die Aufgabe trägt keine Classic-Kennung (' + itemId + ').');
      continue;
    }
    if (!task) {
      liegenLassen(zeilen, 'Diese Aufgabe steht nicht in der geladenen Prüfung (' + itemId + ').');
      continue;
    }

    // ⚠️ `taskScore` und nicht die `QUESTION_RATING`-Zeile: Classics Aufgabenbewertung trägt bei
    // Zeilenbewertung die **Summe** der Antwortzeilen, und die Auswertung liest genau dieses
    // Feld. Wer die Zeile durchreichte, schickte für jede zeilenbewertete Aufgabe eine Null.
    const punkte = taskScore(task, eingabe.assessment);
    const kommentar = eingabe.assessment.comments[itemId]?.text ?? '';
    const id = eingabe.naechsteId();
    const lastUpdate = juengstes(zeilen);

    stapel.taskratings.push({
      id,
      taskID,
      examineeID,
      examinerID: pruefer(zeilen),
      actorID: schauspieler(zeilen),
      points: toNumber(punkte),
      pointsNumerator: punkte.n,
      pointsDenominator: punkte.d,
      commentText: kommentar === '' ? null : kommentar,
      ...kommentarFelder(itemId),
      meta: null,
      transmitted: 'N',
      lastUpdate,
    });
    stapel.gruppen.push({ art: 'task', id, logIds: zeilen.map((z) => z.id), lastUpdate });
  }

  /* ── Stationsbewertungen ──
   *
   * Classics `Stationsbewertung` trägt **keine Punkte**, nur einen Kommentar (Protokollpapier §2).
   * Der Weg dafür stand seit Etappe 2 und wurde leer mitgeschickt; seit dem 2026-09-13 hat er
   * einen Erzeuger.
   */

  for (const [stationRef, zeilen] of zuStation) {
    const stationID = rohkennung(stationRef);
    if (stationID === null) {
      liegenLassen(zeilen, 'Die Station trägt keine Classic-Kennung (' + stationRef + ').');
      continue;
    }
    // Die jüngste Zeile gewinnt: der Kommentar wird beim Tippen fortgeschrieben, und Classic
    // kennt je Station nur einen.
    // Nur Textzeilen tragen den Kommentar — seit dem 2026-10-09 stehen hier auch Medienzeilen,
    // deren `value` eine Anzahl ist.
    const texte = zeilen.filter((z) => !AUFNAHMEN.has(z.examLogTypeID));
    const juengsteZeile = texte.length > 0
      ? texte.reduce((max, z) => (z.creationDate > max.creationDate ? z : max), texte[0])
      : null;
    const text = juengsteZeile?.value ?? eingabe.assessment.comments[stationRef]?.text ?? '';
    const id = eingabe.naechsteId();
    const lastUpdate = juengstes(zeilen);

    stapel.stationratings.push({
      id,
      stationID,
      examineeID,
      examinerID: pruefer(zeilen),
      actorID: schauspieler(zeilen),
      commentText: text === '' ? null : text,
      ...kommentarFelder(stationRef),
      meta: null,
      transmitted: 'N',
      lastUpdate,
    });
    stapel.gruppen.push({ art: 'station', id, logIds: zeilen.map((z) => z.id), lastUpdate });
  }

  /* ── Zeilenbewertungen ── */

  for (const [schlüssel, zeilen] of zuAntwort) {
    const global = schlüssel.startsWith('classic:globalanswer:');
    const itemId = global ? schlüssel.slice('classic:globalanswer:'.length) : antwortGehoertZu.get(schlüssel)?.itemId;
    const task = itemId ? taskVon.get(itemId) : undefined;

    /*
     * ⚠️ **Bei globaler Bewertung sind es womöglich mehrere Zeilen.**
     *
     * Unsere Marken hängen an der **einen** `ExamQuestion` und zählen über den ganzen Block;
     * Classic führt `selectedWordIndexes` je `Zeilenbewertung`, also je Antwortzeile, und zählt
     * dort ab dem Beginn **dieser** Zeile. Die Blockindizes werden deshalb hier verteilt —
     * `verteileWortindexe` weiss aus dem Download, wie viele Wörter jede Zeile beisteuert.
     *
     * Eine Prüfung, die vor dem 2026-09-13 geladen wurde, trägt nur eine nackte Zahl; dann
     * ist es eine Zeile und alles geht wie bisher an sie.
     */
    const globalZeilen = global ? zeilenAusRoh(eingabe.kennungen.get(schlüssel)) : [];
    const answerID = global ? (globalZeilen[0]?.id ?? null) : rohkennung(schlüssel);

    if (answerID === null || !Number.isSafeInteger(answerID) || !itemId || !task) {
      liegenLassen(zeilen, 'Diese Antwortzeile lässt sich keiner Aufgabe zuordnen (' + schlüssel + ').');
      continue;
    }

    const position = global ? -1 : (antwortGehoertZu.get(schlüssel)?.position ?? -1);
    const eintrag = eingabe.index.get(itemId);
    const beschreibung = position >= 0 ? eintrag?.answers[position] : undefined;

    const { punkte, meta } = beschreibung
      ? zeilenPunkte(task, eingabe.assessment, position, beschreibung.max, beschreibung.steps)
      // Die globale Antwortzeile trägt in Relay keine eigenen Punkte — sie stehen am Item und
      // gehen ins Aufgabenbewertung. Hier zählt nur die Wortmarkierung.
      : { punkte: { n: 0, d: 1 } as Fraction, meta: null };

    const lastUpdate = juengstes(zeilen);
    const marken = wortIndexe(eingabe.assessment, itemId);

    /*
     * Wohin die Marken gehen. Im Zeilenfall an die eine Zeile, unverändert. Im globalen Fall
     * an **jede** Zeile, die Marken trägt — Zeilen ohne bleiben weg, ein leeres `Zeilenbewertung`
     * wäre nur Rauschen in der Auswertung.
     *
     * Sind gar keine Marken gesetzt, bleibt es bei **einem** Rating an der ersten Zeile: der
     * Eintrag quittiert dann das Löschen einer vorher gesetzten Markierung.
     */
    const ziele =
      global && marken.length > 0
        ? verteileWortindexe(globalZeilen, marken)
        : [{ id: answerID, indexe: marken }];

    /*
     * ⚠️ **Kein Ziel heisst: liegenlassen, nicht schweigen.** `verteileWortindexe` gibt nichts
     * zurück, wenn keine Marke in eine bewertbare Zeile fällt — etwa weil der Text der Prüfung
     * am Server kürzer geworden ist, seit der Prüfer markiert hat, oder weil die Zeile ohne
     * Kennung geschickt wurde (`abbildung.ts`, Kennung `0`).
     *
     * Ohne diesen Zweig fiele die Schleife einfach durch: die offenen Zeilen kämen weder in den
     * Stapel noch in `liegengeblieben`, blieben also ohne Grund und ohne `storeDate` liegen und
     * klopften bei jedem Takt erneut an.
     */
    if (ziele.length === 0) {
      liegenLassen(
        zeilen,
        'Die Wortmarken dieser Aufgabe treffen keine Antwortzeile des Servers (' + schlüssel + ').',
      );
      continue;
    }

    for (const ziel of ziele) {
      const id = eingabe.naechsteId();
      stapel.answerratings.push({
        id,
        answerID: ziel.id,
        examineeID,
        examinerID: pruefer(zeilen),
        points: toNumber(punkte),
        pointsNumerator: punkte.n,
        pointsDenominator: punkte.d,
        // ⚠️ Kommaliste, kein JSON-Array — der Classic-Client übergibt `indexes.join(',')`.
        selectedWordIndexes: ziel.indexe.join(','),
        meta,
        transmitted: 'N',
        lastUpdate,
      });
      stapel.gruppen.push({ art: 'answer', id, logIds: zeilen.map((z) => z.id), lastUpdate });
    }
  }

  return stapel;
}
